import styled, { css, keyframes } from 'styled-components';
import tw from 'twin.macro';

/* A placeholder for a piece of content that is still loading: a rounded block
 * the size of the text it will be replaced by, with a highlight sweeping over
 * it to say that something is on its way.
 *
 * Skeletons stand in for the content rather than covering it, so a loading
 * screen already has the shape of the screen it becomes and nothing jumps when
 * the data lands: use as many as the loaded thing will have lines, in the sizes
 * those lines will have. A skeleton carries no information of its own — it is
 * hidden from screen readers, so give the region it loads into an accessible
 * name (role="status" and an aria-label) instead. */

/* The heights are the line heights of the Typography components (leading-22,
   leading-20, ...), so a skeleton takes exactly the room its text will take.
   The lineHeight tokens are not in the height scale, hence the raw values. */
const textSizeHeight = {
  title: '1.5rem',
  body: '1.375rem',
  subhead: '1.25rem',
  meta: '1.125rem',
  caption: '1rem'
};

/* The font size of the text being stood in for. A skeleton holds no text, so
   this is only the basis its $charWidth is measured in: one ch is the width of
   a '0' in the font the value itself would have been set in */
const textSizeFont = {
  title: tw`text-xl`,
  body: tw`text-lg`,
  subhead: tw`text-md`,
  meta: tw`text-sm`,
  caption: tw`text-xs`
};

const widthStyle = {
  full: tw`w-full`,
  '3/4': tw`w-3/4`,
  '2/3': tw`w-2/3`,
  '1/2': tw`w-1/2`,
  '1/3': tw`w-1/3`,
  '1/4': tw`w-1/4`
};

export type SkeletonTextSize = keyof typeof textSizeHeight;

export type SkeletonWidth = keyof typeof widthStyle;

export interface SkeletonProps {
  /* The text size this skeleton stands in for; drives its height */
  $textSize?: SkeletonTextSize;
  /* How much of the space it sits in it takes up; a full-width skeleton reads
     as a paragraph, a shorter one as a heading or a value */
  $width?: SkeletonWidth;
  /* For a value whose length is known — a time, a date, a number — the number
     of characters it will have. Two skeletons standing in for values of the
     same kind then come out the same width, the way the values will. Wins over
     $width */
  $charWidth?: number;
  /* A floor in pixels, for a skeleton in a column narrow enough that a share
     of it would read as a dash rather than as a value */
  $minWidth?: number;
}

/* One cycle is a pass of the highlight followed by a pause. The pass covers
   1.5 block widths (half a block of run-up on each side, see sweep), which at
   the speed a sweep reads best as puts it just under two seconds. */
const cycleDuration = '2.4s';

/* How much of the cycle the pass takes; the rest is the pause. Long enough
   that the pause registers as a beat, short enough that the block is never
   plain for so long that it stops looking busy. */
const sweepShare = 0.7;

/* Moves the highlight from just past the right edge to just past the left one,
   then holds it there for the rest of the cycle.
   A percentage background-position places the highlight at that share of
   (block width - highlight width), so with a highlight half the block wide,
   200% is the first position fully off the right edge and -100% the first one
   fully off the left. Between those the block is plain at both ends of a pass,
   and the loop has no seam: there is nothing on screen to jump back.
   The hold turns the wrap into a pause between passes rather than an immediate
   restart, so a row of skeletons reads as a pulse rather than a treadmill. */
const sweep = keyframes`
  0% {
    background-position: 200% 0;
  }
  ${sweepShare * 100}% {
    background-position: -100% 0;
  }
  100% {
    background-position: -100% 0;
  }
`;

const Skeleton = styled.span.attrs({ 'aria-hidden': true })<SkeletonProps>(
  ({ $textSize = 'body', $width = 'full', $charWidth, $minWidth }) => [
    tw`block rounded bg-silver-lighten-20`,
    textSizeFont[$textSize],
    $charWidth === undefined
      ? widthStyle[$width]
      : css`
          width: ${$charWidth}ch;
        `,
    $minWidth !== undefined &&
      css`
        min-width: ${$minWidth}px;
      `,
    css`
      height: ${textSizeHeight[$textSize]};
      /* The block's own colour shows through the transparent ends of the
         gradient; only the white middle sweeps across it */
      background-image: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.65),
        transparent
      );
      background-repeat: no-repeat;
      background-size: 50% 100%;
      /* Linear: a pass at constant speed. Easing would slow the highlight down
         at the edges, which is exactly where it is off the block and there is
         nothing to see happening */
      animation: ${sweep} ${cycleDuration} linear infinite;
    `,
    css`
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `
  ]
);

export default Skeleton;
